// ═══════════════════════════════════════════════════════════════════════════
// Fairness Engine — BuildMyShifts
// Tracks distribution of hours, premium shifts, sleep-ins, and weekends
// across staff. Detects unfairness and suggests rebalancing.
// ═══════════════════════════════════════════════════════════════════════════

import { getBlockWorkingHours, SEGMENT_TYPES, PAY_TYPES } from './shiftTypes';
import { getWeekStart } from './schedulingEngine';

/**
 * Build a distribution summary for all staff over a date range
 * Returns staffId → { totalHours, standardHours, premiumHours, sleepIns,
 *   weekendShifts, nightShifts, bankHolidayShifts, onCallShifts, shiftCount }
 */
export function buildDistribution(shifts, dateRange = {}) {
  const dist = {};

  for (const shift of shifts) {
    if (shift.status === 'cancelled' || !shift.assignedStaffId) continue;
    if (dateRange.start && shift.date < dateRange.start) continue;
    if (dateRange.end && shift.date > dateRange.end) continue;

    const sid = shift.assignedStaffId;
    if (!dist[sid]) {
      dist[sid] = {
        staffId: sid,
        staffName: shift.assignedStaffName || sid,
        totalHours: 0,
        standardHours: 0,
        premiumHours: 0,
        sleepIns: 0,
        weekendShifts: 0,
        nightShifts: 0,
        bankHolidayShifts: 0,
        onCallShifts: 0,
        shiftCount: 0,
      };
    }

    const d = dist[sid];
    d.shiftCount++;

    const segments = shift.segments || [{
      type: SEGMENT_TYPES.STANDARD,
      startTime: shift.startTime,
      endTime: shift.endTime,
      payType: PAY_TYPES.STANDARD,
    }];

    const hours = getBlockWorkingHours(segments, false);
    d.totalHours += hours;

    // Categorise segments
    for (const seg of segments) {
      if (seg.type === SEGMENT_TYPES.SLEEP_IN) {
        d.sleepIns++;
      } else if (seg.type === SEGMENT_TYPES.ON_CALL) {
        d.onCallShifts++;
      }

      if (seg.payType === PAY_TYPES.NIGHT_PREMIUM) d.nightShifts++;
      if (seg.payType === PAY_TYPES.WEEKEND) d.weekendShifts++;
      if (seg.payType === PAY_TYPES.BANK_HOLIDAY) d.bankHolidayShifts++;
      if ([PAY_TYPES.NIGHT_PREMIUM, PAY_TYPES.WEEKEND, PAY_TYPES.BANK_HOLIDAY].includes(seg.payType)) {
        d.premiumHours += getBlockWorkingHours([seg], false);
      } else {
        d.standardHours += getBlockWorkingHours([seg], false);
      }
    }

    // Date-level detection
    const dayOfWeek = new Date(shift.date + 'T12:00:00').getDay();
    if (dayOfWeek === 0 || dayOfWeek === 6) d.weekendShifts++;
  }

  // Round all values
  for (const d of Object.values(dist)) {
    d.totalHours = Math.round(d.totalHours * 100) / 100;
    d.standardHours = Math.round(d.standardHours * 100) / 100;
    d.premiumHours = Math.round(d.premiumHours * 100) / 100;
  }

  return dist;
}

/**
 * Calculate fairness metrics from a distribution
 * Returns { giniCoefficient, stdDeviation, maxMinRatio, outliers, fair }
 */
export function analyseFairness(distribution, metric = 'totalHours') {
  const values = Object.values(distribution).map(d => d[metric] || 0);
  if (values.length <= 1) return { giniCoefficient: 0, stdDeviation: 0, maxMinRatio: 1, outliers: [], fair: true };

  // Mean
  const sum = values.reduce((a, b) => a + b, 0);
  const mean = sum / values.length;
  if (mean === 0) return { giniCoefficient: 0, stdDeviation: 0, maxMinRatio: 1, outliers: [], fair: true };

  // Standard deviation
  const squaredDiffs = values.map(v => Math.pow(v - mean, 2));
  const stdDev = Math.sqrt(squaredDiffs.reduce((a, b) => a + b, 0) / values.length);

  // Gini coefficient (0 = perfectly equal, 1 = perfectly unequal)
  const sorted = [...values].sort((a, b) => a - b);
  let giniSum = 0;
  for (let i = 0; i < sorted.length; i++) {
    giniSum += (2 * (i + 1) - sorted.length - 1) * sorted[i];
  }
  const gini = giniSum / (sorted.length * sum);

  // Max/min ratio
  const max = Math.max(...values);
  const min = Math.min(...values.filter(v => v > 0));
  const ratio = min > 0 ? max / min : Infinity;

  // Outliers: > 1.5 std dev from mean
  const outliers = Object.entries(distribution)
    .filter(([, d]) => Math.abs((d[metric] || 0) - mean) > 1.5 * stdDev)
    .map(([staffId, d]) => ({
      staffId,
      staffName: d.staffName,
      value: d[metric] || 0,
      deviation: Math.round(((d[metric] || 0) - mean) * 100) / 100,
      direction: (d[metric] || 0) > mean ? 'over' : 'under',
    }));

  // Fair if gini < 0.3 and no extreme outliers
  const fair = gini < 0.3 && outliers.length === 0;

  return {
    giniCoefficient: Math.round(gini * 1000) / 1000,
    stdDeviation: Math.round(stdDev * 100) / 100,
    mean: Math.round(mean * 100) / 100,
    maxMinRatio: Math.round(ratio * 100) / 100,
    outliers,
    fair,
  };
}

/**
 * Run fairness analysis across all key metrics
 */
export function fullFairnessReport(shifts, dateRange) {
  const distribution = buildDistribution(shifts, dateRange);
  const staffList = Object.values(distribution);

  return {
    distribution,
    staffCount: staffList.length,
    metrics: {
      totalHours: analyseFairness(distribution, 'totalHours'),
      premiumHours: analyseFairness(distribution, 'premiumHours'),
      sleepIns: analyseFairness(distribution, 'sleepIns'),
      weekendShifts: analyseFairness(distribution, 'weekendShifts'),
      nightShifts: analyseFairness(distribution, 'nightShifts'),
      shiftCount: analyseFairness(distribution, 'shiftCount'),
    },
    overallFair: ['totalHours', 'premiumHours', 'sleepIns', 'weekendShifts'].every(
      m => analyseFairness(distribution, m).fair
    ),
  };
}

/**
 * Suggest rebalancing moves to improve fairness
 * Returns array of { suggestion, fromStaffId, toStaffId, metric, impact }
 */
export function suggestRebalancing(shifts, dateRange, allStaff) {
  const report = fullFairnessReport(shifts, dateRange);
  const suggestions = [];

  for (const [metric, analysis] of Object.entries(report.metrics)) {
    if (analysis.fair) continue;

    const overWorked = analysis.outliers.filter(o => o.direction === 'over');
    const underWorked = analysis.outliers.filter(o => o.direction === 'under');

    for (const over of overWorked) {
      for (const under of underWorked) {
        const readableMetric = metric
          .replace('totalHours', 'total hours')
          .replace('premiumHours', 'premium hours')
          .replace('sleepIns', 'sleep-in shifts')
          .replace('weekendShifts', 'weekend shifts')
          .replace('nightShifts', 'night shifts')
          .replace('shiftCount', 'shift count');

        suggestions.push({
          suggestion: `Move some ${readableMetric} from ${over.staffName} to ${under.staffName}`,
          fromStaffId: over.staffId,
          fromStaffName: over.staffName,
          toStaffId: under.staffId,
          toStaffName: under.staffName,
          metric,
          fromValue: over.value,
          toValue: under.value,
          expectedImprovement: Math.round(((over.value - under.value) / 2) * 100) / 100,
        });
      }
    }
  }

  // Deduplicate similar suggestions
  const seen = new Set();
  return suggestions.filter(s => {
    const key = `${s.fromStaffId}-${s.toStaffId}-${s.metric}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

/**
 * Calculate a sleep-in fairness score specifically
 * Returns per-staff sleep-in count and distribution analysis
 */
export function sleepInFairness(shifts, dateRange) {
  const dist = buildDistribution(shifts, dateRange);
  const analysis = analyseFairness(dist, 'sleepIns');
  const staffSleepIns = Object.values(dist)
    .map(d => ({ staffId: d.staffId, staffName: d.staffName, sleepIns: d.sleepIns }))
    .sort((a, b) => b.sleepIns - a.sleepIns);

  return {
    staffSleepIns,
    analysis,
    maxPerStaff: staffSleepIns[0]?.sleepIns || 0,
    minPerStaff: staffSleepIns[staffSleepIns.length - 1]?.sleepIns || 0,
  };
}

const fairnessEngine = {
  buildDistribution,
  analyseFairness,
  fullFairnessReport,
  suggestRebalancing,
  sleepInFairness,
};
export default fairnessEngine;
